import { useState, useEffect } from 'react';
import { Globe, FileCheck, Plane, Languages, Hotel, DollarSign } from 'lucide-react';
import { whyChooseUs } from '../mockData';

const iconMap = {
    Globe,
    FileCheck,
    Plane,
    Languages,
    Hotel,
    DollarSign
};

export const WhyChooseUs = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    }
                });
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById('why-choose-us');
        if (element) observer.observe(element);

        return () => {
            if (element) observer.unobserve(element);
        };
    }, []);

    return (
        <section id="why-choose-us" className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden relative">
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-brand-blue/5 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-brand-teal/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div
                    className={`text-center mb-20 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                        }`}
                >
                    <div className="inline-block px-5 py-2 bg-brand-blue/10 text-brand-navy rounded-full text-sm font-bold mb-6 tracking-wide uppercase hover:bg-brand-blue hover:text-white transition-colors duration-300">
                        Our Promise to You
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                        Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-teal">Bharat Heal?</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        We provide comprehensive support for international patients throughout their medical journey, ensuring comfort, care, and peace of mind.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {whyChooseUs.map((item, index) => {
                        const IconComponent = iconMap[item.icon];
                        return (
                            <div
                                key={item.id}
                                className={`transform transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                                    }`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="group h-full bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-2xl hover:border-brand-blue/20 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden">

                                    {/* Hover Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 via-transparent to-brand-teal/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    {/* Decorative Circle */}
                                    <div className="absolute -right-8 -top-8 w-24 h-24 bg-brand-blue/5 rounded-full group-hover:scale-150 transition-transform duration-700 ease-in-out"></div>

                                    <div className="relative flex flex-col items-start gap-6">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-teal flex items-center justify-center shadow-lg shadow-brand-blue/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ring-4 ring-white">
                                            <IconComponent className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-110" />
                                        </div>

                                        <div className="space-y-3">
                                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-brand-blue transition-colors duration-300">
                                                {item.title}
                                            </h3>
                                            <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                                                {item.description}
                                            </p>
                                        </div>

                                        {/* Bottom accent line */}
                                        <div className="w-12 h-1 bg-gray-200 mt-2 rounded-full group-hover:w-full group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-brand-teal transition-all duration-500"></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
